import { axiosInstance } from '../common/api';

/**
 * Create a new WFH request
 */
export const createWfhRequest = (data) => {
  const url = `/wfh/`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .post(url, data)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Get all WFH requests for current user
 */
export const getMyWfhRequests = (filters = {}, pagination = {}) => {
  const params = new URLSearchParams();

  if (filters.status) params.append('status', filters.status);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

  if (pagination.page) params.append('page', pagination.page);
  if (pagination.pageSize) params.append('pageSize', pagination.pageSize);

  const queryString = params.toString();
  const url = `/wfh/my-wfh${queryString ? `?${queryString}` : ''}`;

  return new Promise((resolve, reject) => {
    axiosInstance
      .get(url)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Get all companies (Super Admin only)
 */
export const getAllCompanies = () => {
  const url = `/wfh/companies`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .get(url)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Get reviewers (managers and HR) for WFH request
 */
export const getReviewers = (companyId = null) => {
  const params = new URLSearchParams();
  if (companyId) params.append('companyId', companyId);
  const queryString = params.toString();
  const url = `/wfh/reviewers${queryString ? `?${queryString}` : ''}`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .get(url)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Get all WFH requests (Admin/Manager/HR)
 */
export const getAllWfhRequests = (filters = {}, pagination = {}) => {
  const params = new URLSearchParams();

  if (filters.status) params.append('status', filters.status);
  if (filters.employeeName) params.append('employeeName', filters.employeeName);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
  if (filters.companyId) params.append('companyId', filters.companyId);

  if (pagination.page) params.append('page', pagination.page);
  if (pagination.pageSize) params.append('pageSize', pagination.pageSize);

  const queryString = params.toString();
  const url = `/wfh/${queryString ? `?${queryString}` : ''}`;

  return new Promise((resolve, reject) => {
    axiosInstance
      .get(url)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Get a single WFH request by ID
 */
export const getWfhById = (id) => {
  const url = `/wfh/${id}`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .get(url)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Update WFH request (Employee - own pending WFH only)
 */
export const updateWfhRequest = (id, data) => {
  const url = `/wfh/${id}`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .put(url, data)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Update WFH status (Approve/Reject) - Admin/Manager/HR only
 */
export const updateWfhStatus = (id, data) => {
  const url = `/wfh/${id}/status`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .put(url, data)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Approve several WFH requests at once - Admin/Manager only (rejections stay per request)
 * @param {Object} data - { ids: number[], status: 'approved' }
 * Resolves with data.data = { succeeded: number[], failed: [{ id, message }] }
 */
export const bulkUpdateWfhStatus = (data) => {
  const url = `/wfh/bulk-status`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .put(url, data)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Cancel an approved WFH request (HR/Admin/Manager only)
 */
export const cancelApprovedWfh = (id, data = {}) => {
  const url = `/wfh/${id}/cancel-approved`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .put(url, data)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Cancel WFH request (Employee - own pending WFH only)
 */
export const cancelWfhRequest = (id) => {
  const url = `/wfh/${id}/cancel`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .put(url)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Export WFH requests to Excel - exports exactly what the list shows for these filters
 * @param {Object} filters - Filters object (status, companyId, employeeName, startDate, endDate)
 */
export const exportWfhRequests = (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.status) params.append('status', filters.status);
  if (filters.companyId) params.append('companyId', filters.companyId);
  if (filters.employeeName) params.append('employeeName', filters.employeeName);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);

  const url = `/wfh/export?${params.toString()}`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .get(url, { responseType: 'blob' })
      .then((response) => {
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;

        const contentDisposition = response.headers['content-disposition'];
        let filename = `WFH_Export.xlsx`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);

        resolve(response);
      })
      .catch((err) => reject(err));
  });
};
